// Sprint 097 — Roamer Collections™ Service
// Smart collections auto-derived from Backpack + Vault item content.
// Items land in a collection when their title/tags/keywords match the pattern.

import { getAllBackpackItems } from './RoamerBackpackService';
import { getAllVaultItems } from './RoamerVaultService';

export interface RoamerCollection {
  id: string;
  name: string;
  /** Lucide icon name (used to pick icon in UI) */
  iconName: string;
  /** Hex color for the tile */
  color: string;
  /** Number of matched items */
  count: number;
  /** Example items for preview */
  previewTitles: string[];
}

// ── Collection templates ──────────────────────────────────────────────────────

interface CollectionTemplate {
  id: string;
  name: string;
  iconName: string;
  color: string;
  /** lower-case keywords matched against title + tags + keywords */
  patterns: string[];
}

const TEMPLATES: CollectionTemplate[] = [
  {
    id: 'ai-productivity',
    name: 'AI & Productivity',
    iconName: 'brain',
    color: '#a855f7',
    patterns: ['ai', 'artificial intelligence', 'chatgpt', 'openai', 'claude', 'automation',
               'productivity', 'workflow', 'llm', 'gpt', 'machine learning', 'deep learning'],
  },
  {
    id: 'thailand',
    name: 'Thailand',
    iconName: 'palmtree',
    color: '#22c55e',
    patterns: ['thailand', 'thai', 'chiang mai', 'bangkok', 'phuket', 'koh samui', 'pattaya', 'chiangmai'],
  },
  {
    id: 'online-income',
    name: 'Online Income',
    iconName: 'dollar-sign',
    color: '#22c55e',
    patterns: ['income', 'earning', 'money', 'freelance', 'side hustle', 'passive income',
               'online work', 'remote work', 'gig economy', 'digital nomad income', 'make money'],
  },
  {
    id: 'travel-visas',
    name: 'Travel & Visas',
    iconName: 'plane',
    color: '#3b82f6',
    patterns: ['travel', 'visa', 'passport', 'immigration', 'border', 'flight', 'destination',
               'country', 'nomad', 'expat', 'remote year'],
  },
  {
    id: 'investing',
    name: 'Investing',
    iconName: 'trending-up',
    color: '#f59e0b',
    patterns: ['invest', 'stock', 'crypto', 'bitcoin', 'ethereum', 'etf', 'finance', 'trading',
               'market', 'portfolio', 'dividend', 'compound interest', 'wealth'],
  },
  {
    id: 'digital-nomad',
    name: 'Digital Nomad',
    iconName: 'globe',
    color: '#06b6d4',
    patterns: ['digital nomad', 'remote lifestyle', 'location independent', 'co-working',
               'coliving', 'workation', 'slow travel', 'nomadic'],
  },
  {
    id: 'freelancing',
    name: 'Freelancing',
    iconName: 'briefcase',
    color: '#8b5cf6',
    patterns: ['freelance', 'upwork', 'fiverr', 'toptal', 'client', 'contract', 'proposal',
               'portfolio', 'rate', 'hourly', 'agency'],
  },
  {
    id: 'health-wellness',
    name: 'Health & Wellness',
    iconName: 'heart',
    color: '#ef4444',
    patterns: ['health', 'wellness', 'fitness', 'gym', 'diet', 'nutrition', 'sleep',
               'mental health', 'meditation', 'yoga', 'running', 'exercise'],
  },
  {
    id: 'business',
    name: 'Business',
    iconName: 'building-2',
    color: '#64748b',
    patterns: ['business', 'startup', 'entrepreneur', 'saas', 'revenue', 'marketing',
               'growth hacking', 'product', 'sales', 'b2b', 'ecommerce', 'shopify'],
  },
  {
    id: 'tools-software',
    name: 'Tools & Software',
    iconName: 'cpu',
    color: '#14b8a6',
    patterns: ['tool', 'software', 'app', 'plugin', 'extension', 'automation', 'notion',
               'airtable', 'zapier', 'make', 'api', 'integration', 'tech stack'],
  },
];

// ── Matching ──────────────────────────────────────────────────────────────────

function matchesTemplate(
  haystack: string,
  template: CollectionTemplate
): boolean {
  const lower = haystack.toLowerCase();
  return template.patterns.some(p => lower.includes(p));
}

function buildHaystack(item: { title?: string; tags?: string[]; keywords?: string[]; contentText?: string | null }): string {
  return [
    item.title ?? '',
    ...(item.tags ?? []),
    ...(item.keywords ?? []),
    item.contentText ?? '',
  ].join(' ');
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Derive smart collections from the current backpack + vault state. */
export function getSmartCollections(): RoamerCollection[] {
  const backpackItems = getAllBackpackItems();
  const vaultItems = getAllVaultItems();

  // Combine all searchable text
  const allHaystacks: { haystack: string; title: string }[] = [
    ...backpackItems.map(i => ({
      haystack: buildHaystack({ title: i.title, contentText: i.contentText }),
      title: i.title,
    })),
    ...vaultItems.map(i => ({
      haystack: buildHaystack({ title: i.title, tags: i.tags, keywords: i.keywords }),
      title: i.title,
    })),
  ];

  return TEMPLATES.map(template => {
    const matched = allHaystacks.filter(h => matchesTemplate(h.haystack, template));
    return {
      id: template.id,
      name: template.name,
      iconName: template.iconName,
      color: template.color,
      count: matched.length,
      previewTitles: matched.slice(0, 3).map(m => m.title),
    };
  }).sort((a, b) => b.count - a.count);
}

/** Total number of non-empty collections. */
export function getCollectionCount(): number {
  return getSmartCollections().filter(c => c.count > 0).length;
}

/** Total storage used by all backpack file items in bytes. */
export function getTotalStorageBytes(): number {
  const items = getAllBackpackItems();
  return items.reduce((sum, i) => sum + (i.fileSize ?? 0), 0);
}

/** Estimated knowledge time saved: 5 min per item processed. */
export function getTimeSavedMinutes(): number {
  const items = getAllBackpackItems();
  const processed = items.filter(i => i.processingStatus === 'complete').length;
  return processed * 5;
}
